import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import JudgeHeader from '../../components/JudgeHeader';
import AudioControls from '../../components/AudioControls';
import Tooltip from '../../components/Tooltip';
import './judge.css';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function EvalBadge({ status, ready }) {
  if (status === 'submitted') {
    return <span className="badge badge--submitted">submitted</span>;
  }
  if (ready) {
    return (
      <span style={{
        background:   '#22c55e',
        color:        '#fff',
        borderRadius: '4px',
        padding:      '2px 8px',
        fontSize:     '0.75rem',
        fontWeight:   700,
      }}>
        ✓ Ready
      </span>
    );
  }
  if (status === 'draft') {
    return <span className="badge badge--draft">draft</span>;
  }
  return null;
}

function formatDate(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day:   'numeric',
    year:  'numeric',
  });
}

// Stored audio URLs are absolute (http://host/...) — extract just the pathname
// so they resolve correctly from any host in the browser.
function audioPath(url) {
  if (!url) return null;
  try { return new URL(url).pathname; } catch { return url; }
}

// The play button (with its download button) is the shared
// components/AudioControls, so it looks the same here as on the admin pages.

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function JudgeDashboard() {
  const [data, setData]                         = useState(null);
  const [loading, setLoading]                   = useState(true);
  const [error, setError]                       = useState('');
  const [placementCategories, setPlacementCategories] = useState([]);

  // Per-assignment designation state
  const [designations, setDesignations] = useState({});   // { assignment_id: placement_category_id as string | '' }
  const [savingDesig, setSavingDesig]   = useState({});   // { assignment_id: bool }
  const [desigError, setDesigError]     = useState({});   // { assignment_id: string }

  // Audio playback — only one song plays at a time
  const [playingId, setPlayingId] = useState(null); // song_id of currently playing audio
  const playingIdRef = useRef(null);                // ref copy for the unmount cleanup
  playingIdRef.current = playingId;

  useEffect(() => {
    Promise.all([
      api.get('/api/judges/dashboard'),
      api.get('/api/judges/placement-categories'),
    ])
      .then(([dashRes, catRes]) => {
        setData(dashRes.data);
        setPlacementCategories(catRes.data.categories ?? []);
        // Seed designation state from server (store as string for <select> value)
        const init = {};
        (dashRes.data.assignments ?? []).forEach((a) => {
          init[a.assignment_id] =
            a.placement_category_id != null ? String(a.placement_category_id) : '';
        });
        setDesignations(init);
      })
      .catch(() => setError('Failed to load dashboard. Please try refreshing.'))
      .finally(() => setLoading(false));
  }, []);

  // Stop audio when the judge navigates away from the dashboard
  useEffect(() => {
    return () => {
      if (playingIdRef.current) {
        const el = document.getElementById(`audio-${playingIdRef.current}`);
        if (el) { el.pause(); el.currentTime = 0; }
      }
    };
  }, []); // empty deps — cleanup only on unmount

  function toggleAudio(songId, shouldPlay) {
    // Stop whatever is currently playing (if different song)
    if (playingId && playingId !== songId) {
      const prev = document.getElementById(`audio-${playingId}`);
      if (prev) { prev.pause(); prev.currentTime = 0; }
    }
    const audio = document.getElementById(`audio-${songId}`);
    if (!audio) return;
    if (shouldPlay) { audio.play(); setPlayingId(songId); }
    else            { audio.pause(); audio.currentTime = 0; setPlayingId(null); }
  }

  async function handleDesignation(assignmentId, value) {
    setDesignations((prev) => ({ ...prev, [assignmentId]: value }));
    setSavingDesig((prev) => ({ ...prev, [assignmentId]: true }));
    setDesigError((prev) => ({ ...prev, [assignmentId]: '' }));

    try {
      await api.post(`/api/judges/designate/${assignmentId}`, {
        placement_category_id: value ? parseInt(value, 10) : null,
      });
    } catch (err) {
      // Revert optimistic update on failure
      setDesignations((prev) => {
        const original = data?.assignments?.find(
          (a) => a.assignment_id === assignmentId
        );
        const revert = original?.placement_category_id != null
          ? String(original.placement_category_id)
          : '';
        return { ...prev, [assignmentId]: revert };
      });
      setDesigError((prev) => ({
        ...prev,
        [assignmentId]: err.response?.data?.error ?? 'Could not save designation.',
      }));
    } finally {
      setSavingDesig((prev) => ({ ...prev, [assignmentId]: false }));
    }
  }

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="c-page">
        <JudgeHeader />
        <main className="c-main">
          <div className="c-loading">
            <div className="c-spinner" aria-label="Loading…" />
            <p>Loading your dashboard…</p>
          </div>
        </main>
      </div>
    );
  }

  // ── Error ─────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="c-page">
        <JudgeHeader />
        <main className="c-main">
          <div className="c-alert" role="alert">{error}</div>
        </main>
      </div>
    );
  }

  const { stats, assignments } = data;
  const progressPct = stats.total_assigned > 0
    ? Math.round((stats.evaluations_submitted / stats.total_assigned) * 100)
    : 0;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="c-page">
      <JudgeHeader />

      <main className="c-main">
        <div className="c-section-title">Judge Dashboard</div>

        {/* Competition status banners */}
        {data.competition_status === 'all_paused' && (
          <div className="c-alert c-alert--warning" role="status">
            The competition is currently paused.
          </div>
        )}
        {data.competition_status === 'finished' && (
          <div className="c-alert c-alert--warning" role="status">
            This competition has been finalised. Scoring is now closed.
          </div>
        )}

        {/* ── Stats bar ── */}
        <div className="j-stats">
          <div className="j-stat">
            <span className="j-stat__num">{stats.total_assigned}</span>
            <span className="j-stat__label">Assigned</span>
          </div>
          <div className="j-stat">
            <span className="j-stat__num">{stats.evaluations_submitted}</span>
            <span className="j-stat__label">Submitted</span>
          </div>
          <div className="j-stat">
            <span className="j-stat__num">{stats.evaluations_draft}</span>
            <span className="j-stat__label">Draft</span>
          </div>
          <div className="j-stat">
            <span className="j-stat__num">{stats.evaluations_pending}</span>
            <span className="j-stat__label">Pending</span>
          </div>
          <div className="j-stat j-stat--progress">
            <span className="j-stat__label">
              Progress — {progressPct}% complete
            </span>
            <div className="j-progress">
              <div
                className="j-progress__bar"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
        </div>

        {/* ── Assignment list ── */}
        <h2 className="c-list-heading">My Assignments</h2>

        {assignments.length === 0 ? (
          <div className="c-empty">
            <div className="c-empty__icon">🎵</div>
            <h3 className="c-empty__title">No songs assigned yet</h3>
            <p className="c-empty__text">
              Songs assigned to you for evaluation will appear here.
            </p>
          </div>
        ) : (
          <div className="j-assignment-list">
            {assignments.map((a) => (
              <div
                key={a.assignment_id}
                className={`j-assignment-card j-assignment-card--${a.evaluation_status}`}
              >
                {/* Left: song info */}
                <div className="j-assignment-card__main">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <AudioControls
                      songId={a.song_id}
                      audioUrl={a.audio_url ? audioPath(a.audio_url) : null}
                      playingId={playingId}
                      onToggle={toggleAudio}
                    />
                    <p className="j-assignment-card__title" style={{ margin: 0 }}>{a.title}</p>
                  </div>
                  <div className="j-assignment-card__meta">
                    <span>{a.category_name}</span>
                    {a.submitted_at && (
                      <>
                        <span className="j-dot">·</span>
                        <span>Scored {formatDate(a.submitted_at)}</span>
                      </>
                    )}
                  </div>
                </div>

                {/* Right: badge + designation + action */}
                <div className="j-assignment-card__right">
                  <EvalBadge status={a.evaluation_status} ready={a.ready_for_submission} />

                  {/* Score summary — shown for submitted and draft evaluations */}
                  {a.overall_avg != null && (
                    <div className="j-score-summary">
                      <span className="j-score-summary__label">Avg score</span>
                      <span className="j-score-summary__value">{a.overall_avg}</span>
                      <span className="j-score-summary__sub">
                        across {a.score_count} {a.score_count === 1 ? 'criterion' : 'criteria'}
                      </span>
                    </div>
                  )}

                  {/* Designation dropdown — only after evaluation submitted */}
                  {a.evaluation_status === 'submitted' && (
                    <div className="j-designate-wrap">
                      <div style={{ fontSize: '0.72rem', color: '#6b7280', marginBottom: '0.2rem', display: 'flex', alignItems: 'center', gap: '2px' }}>
                        Designation
                        <Tooltip tooltipKey="judge_winner" />
                        <Tooltip tooltipKey="judge_runner_up" />
                      </div>
                      <select
                        className="j-designate-select"
                        value={designations[a.assignment_id] ?? ''}
                        onChange={(e) =>
                          handleDesignation(a.assignment_id, e.target.value)
                        }
                        disabled={savingDesig[a.assignment_id]}
                        aria-label="Placement category"
                      >
                        <option value="">— No designation —</option>
                        {placementCategories.map((cat) => (
                          <option key={cat.id} value={String(cat.id)}>
                            {cat.name}
                          </option>
                        ))}
                      </select>
                      {desigError[a.assignment_id] && (
                        <p className="j-designate-error">
                          {desigError[a.assignment_id]}
                        </p>
                      )}
                    </div>
                  )}

                  <Link
                    to={`/judge/evaluate/${a.song_id}`}
                    className="c-btn"
                  >
                    {a.evaluation_status === 'pending'   ? 'Start judging'    :
                     a.evaluation_status === 'submitted' ? 'View'             :
                                                           'Continue judging'}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
